import { SHELF_SECTIONS } from "@ohc/shared";
import { type FormEvent, Fragment, useEffect, useMemo, useState } from "react";
import {
  ApiError,
  type ItemType,
  type Location,
  type Lot,
  type OwnerKind,
  type Product,
  type ProductBatch,
  api,
  formatMoney,
} from "./api";
import { ageOf, singaporeDay, singaporeToday } from "./dates";
import { batchesOf, workingOf } from "./working";
import { shrink } from "./pos/photos";
import { PageHeader } from "./layout/Layout";
import { StockImport } from "./StockImport";
import { StockPanel } from "./StockPanel";
import { ChoiceField, SelectField } from "./fields";
import {
  Actions,
  Alert,
  Badge,
  Box,
  Empty,
  Field,
  IconAction,
  Loading,
  Modal,
  ModalActions,
  Pager,
  StatCard,
  Table,
  Th,
  TwoLine,
  countOf,
  usePaged,
  Working,
} from "./ui";
import { productCode } from "./productCode";
import { productName } from "./productName";
import { PRODUCT_TYPES, contentsSentence, holdsThings, packsInside, typeOf } from "./productTypes";
import { ProductPictures } from "./ProductPictures";
import { useReading } from "./reading";
import { FlagButton, flaggedFirst } from "./FlagButton";
import { PictureQueue } from "./PictureQueue";
import { useBooks } from "./books";
import { CROP_MARGIN, type CertBox, boxWithin, cutPhoto, useCoveredPhoto } from "./certCover";
import { FindProductPicture } from "./FindProductPicture";

/**
 * Stock (quotation A13 to A17), laid out as the template's list page: figures across the
 * top, then the stock table, then the products.
 *
 * Cost columns appear only for roles allowed to see them. That decision is the server's:
 * for sales staff the figures are not in what arrives at all.
 *
 * On a narrow screen the template scrolls a table sideways, so the quantity sits right
 * after the item name, where it is still on screen without scrolling.
 */
export function Stock({
  canManage,
  canSetPrice,
  canAdjust,
  canBuyFromStaff,
  canCheck,
}: {
  readonly canManage: boolean;
  /** A supervisor: the product list for its prices, and nothing else (D, 8 October 2026). */
  readonly canSetPrice: boolean;
  readonly canAdjust: boolean;
  readonly canBuyFromStaff: boolean;
  /** Admins and masters say a batch has been checked against the shelf (A20). */
  readonly canCheck: boolean;
}): React.ReactElement {
  const [lots, setLots] = useState<Lot[]>([]);
  /** Every slab photo cut close and its cert covered, in one go (D, 10 October 2026). */
  const [tidying, setTidying] = useState(false);
  const [picturing, setPicturing] = useState(false);
  const canTidy = useReading();
  const [value, setValue] = useState<Awaited<ReturnType<typeof api.stock>>["value"]>(null);
  const [attention, setAttention] = useState({ incomplete: 0, negative: 0 });
  const [seesCost, setSeesCost] = useState(false);
  const [locations, setLocations] = useState<Location[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [search, setSearch] = useState("");
  // Company stock or personally funded stock, by the switch at the top (D, 12 October 2026).
  const books = useBooks();
  const owner: OwnerKind = books === "personal" ? "person" : "company";
  const [locationId, setLocationId] = useState("");
  const [show, setShow] = useState<"all" | "finish" | "check">("all");
  const toFinish = show === "finish";
  const toCheck = show === "check";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [openLotId, setOpenLotId] = useState<string | undefined>();
  const [modal, setModal] = useState<"product" | "location" | "import" | undefined>();
  const [notice, setNotice] = useState<string | undefined>();

  async function reload(): Promise<void> {
    try {
      const [stock, places, catalog] = await Promise.all([
        api.stock({ search, owner, locationId, toFinish, toCheck }),
        api.locations(),
        api.products(),
      ]);
      setLots(stock.lots);
      setValue(stock.value);
      setAttention(stock.attention);
      setSeesCost(stock.seesCost);
      setLocations(places.locations);
      setProducts(catalog.products);
      setError(undefined);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not load inventory");
    } finally {
      setLoading(false);
    }
  }

  // Searching as someone types, but not on every key: a short pause, then one request.
  useEffect(() => {
    const timer = window.setTimeout(() => void reload(), search ? 250 : 0);
    return () => window.clearTimeout(timer);
  }, [search, owner, locationId, toFinish, toCheck]);

  const paged = usePaged(useMemo(() => flaggedFirst(lots), [lots]));
  const productLanguages = useMemo(() => new Map(products.map((product) => [product.id, product.language])), [products]);

  if (loading) return <Loading />;

  async function check(lot: Lot): Promise<void> {
    try {
      await api.verifyLot(lot.id);
      setNotice((lot.productName ?? lot.slabName ?? "That batch") + " verified.");
      await reload();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not mark the batch as verified");
    }
  }

  const closeModal = (): void => setModal(undefined);
  const afterSave = (message?: string): void => {
    setModal(undefined);
    if (message) setNotice(message);
    void reload();
  };

  return (
    <>
      <PageHeader
        title={books === "personal" ? "Inventory - personal books" : "Inventory"}
        subtitle="Inventory batches and the product catalogue. Personally funded inventory is visible only to the person who paid for it and to system administrators."
        actions={
          canManage ? (
            <>
              <button type="button" className="ti-btn ti-btn-primary-full" onClick={() => setModal("product")}>
                <i className="ri-add-line" /> Add product
              </button>
              <button type="button" className="ti-btn ti-btn-light" onClick={() => setModal("location")}>
                <i className="ri-map-pin-add-line" /> Add location
              </button>
              {canAdjust ? (
                <button type="button" className="ti-btn ti-btn-light" onClick={() => setModal("import")}>
                  <i className="ri-file-excel-2-line" /> Import product or inventory list
                </button>
              ) : null}
            </>
          ) : undefined
        }
      />

      {error ? <Alert tone="danger">{error}</Alert> : null}
      {notice ? (
        <Alert tone="success" onClose={() => setNotice(undefined)}>
          {notice}
        </Alert>
      ) : null}

      <div className="grid grid-cols-12 gap-x-6">
        {seesCost && value ? (
          <>
            <div className="xl:col-span-3 md:col-span-6 col-span-12">
              <StatCard
                label="Company inventory value at cost"
                value={formatMoney(value.company.valueCents)}
                icon="ti ti-building-store"
                tone="primary"
                note={`${countOf(value.company.units, "unit", "units")} in ${countOf(value.company.lots, "batch", "batches")}`}
              />
            </div>
            <div className="xl:col-span-3 md:col-span-6 col-span-12">
              <StatCard
                label="Personally funded inventory"
                value={formatMoney(value.personal.valueCents)}
                icon="ti ti-user"
                tone="secondary"
                note={`${value.personal.units} units, ${value.free.units} received free of charge`}
              />
            </div>
          </>
        ) : null}
        <div className={seesCost && value ? "xl:col-span-3 md:col-span-6 col-span-12" : "md:col-span-6 col-span-12"}>
          <StatCard
            label="Incomplete records"
            value={attention.incomplete}
            icon="ti ti-edit"
            tone="warning"
            note="Quick entries awaiting details"
          />
        </div>
        <div className={seesCost && value ? "xl:col-span-3 md:col-span-6 col-span-12" : "md:col-span-6 col-span-12"}>
          <StatCard
            label="Negative inventory"
            value={attention.negative}
            icon="ti ti-alert-triangle"
            tone={attention.negative ? "danger" : "success"}
            note={attention.negative ? "More sold than recorded on hand" : "No negative inventory"}
            {...(attention.negative ? { noteTone: "danger" as const } : {})}
          />
        </div>
      </div>

      {tidying ? <TidySlabs onClose={() => setTidying(false)} onDone={() => void reload()} /> : null}
      {picturing ? <PictureQueue onClose={() => setPicturing(false)} onDone={() => void reload()} /> : null}
      <Box
        title="Inventory on hand"
        count={lots.length}
        actions={
          canManage && canTidy ? (
            <div className="flex flex-wrap gap-2">
              {/* D, 14 October 2026: pick each single's picture from an online search, one after another. */}
              <button type="button" className="ti-btn ti-btn-light !py-1 !px-3 !text-[0.75rem] !mb-0" onClick={() => setPicturing(true)}>
                <i className="ri-image-add-line" /> Find pictures for singles
              </button>
              <button type="button" className="ti-btn ti-btn-light !py-1 !px-3 !text-[0.75rem] !mb-0" onClick={() => setTidying(true)}>
                <i className="ri-crop-line" /> Tidy slab photos
              </button>
            </div>
          ) : undefined
        }
      >
        <div className="grid grid-cols-12 gap-4 mb-4">
          <div className="xl:col-span-4 col-span-12">
            <div className="input-group">
              <span className="input-group-text">
                <i className="ri-search-line" />
              </span>
              <input
                id="stock-search"
                type="text"
                className="form-control"
                value={search}
                placeholder="Search by product code, name, batch or certificate number"
                aria-label="Search stock"
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
          </div>
          <div className="xl:col-span-3 sm:col-span-4 col-span-12">
            <div className={`form-control flex items-center gap-2 ${books === "personal" ? "!border-secondary/40" : ""}`}>
              <i className={books === "personal" ? "ri-user-line text-secondary" : "ri-building-line text-primary"} />
              <span className="font-semibold">{books === "personal" ? "Personally funded inventory" : "Company inventory"}</span>
            </div>
          </div>
          <div className="xl:col-span-3 sm:col-span-4 col-span-12">
            <SelectField
              id="stock-location"
              label="Where"
              value={locationId || "all"}
              options={[
                { value: "all", label: "All locations" },
                ...locations.map((location) => ({ value: location.id, label: location.name })),
              ]}
              onChange={(choice) => setLocationId(choice === "all" ? "" : choice)}
            />
          </div>
          <div className="xl:col-span-2 sm:col-span-4 col-span-12">
            <SelectField<"all" | "finish" | "check">
              id="stock-finish"
              label="Show"
              value={show}
              options={[
                { value: "all", label: "All batches" },
                { value: "finish", label: `Incomplete records (${attention.incomplete})` },
                { value: "check", label: "Awaiting verification" },
              ]}
              onChange={setShow}
            />
          </div>
        </div>

        {lots.length === 0 ? (
          <Empty icon="ri-archive-line">
            {toFinish && !search && !owner && !locationId
              ? "No incomplete records."
              : search || owner || locationId || toFinish
              ? "No inventory matches the current filters."
              : "No inventory yet. Inventory is added through a supplier purchase or an inventory list import."}
          </Empty>
        ) : (
          <Table
            head={
              <>
                <Th>Item</Th>
                <Th end>Quantity on hand</Th>
                <Th>Batch</Th>
                <Th>Owner</Th>
                <Th>Location</Th>
                {seesCost ? <Th end>Cost</Th> : null}
                {seesCost ? <Th end>Unit cost</Th> : null}
                <Th>Status</Th>
                <Th end>Action</Th>
              </>
            }
          >
            {paged.rows.map((lot) => (
              <tr
                key={lot.id}
                className={`border-b border-defaultborder cursor-pointer ${lot.flagged ? "bg-danger/[0.06]" : ""}`}
                onClick={() => setOpenLotId(lot.id)}
              >
                <td>
                  <div className="flex items-center gap-2">
                  <FlagButton kind="stock_lot" id={lot.id} flagged={Boolean(lot.flagged)} note={lot.flagNote} onChanged={() => void reload()} />
                  {/* The same picture the catalogue shows, larger on hover (D, 8 October 2026). */}
                  {lot.photoId ? (
                    <span onClick={(event) => event.stopPropagation()}>
                      <PhotoPeek photoId={lot.photoId} cover={lot.certCover ?? null} />
                    </span>
                  ) : null}
                  <TwoLine
                    main={lot.kind === "slab" ? productName({ name: lot.slabName ?? "Graded card", language: lot.slabLanguage }) : productName({ name: lot.productName ?? "Unknown", language: productLanguages.get(lot.productId ?? "") })}
                    sub={
                      lot.kind === "slab"
                        ? lot.certNo
                          ? `Certificate ${lot.certNo}`
                          : "No certificate number yet"
                        : (lot.productCode ?? "")
                    }
                  />
                  </div>
                </td>
                <td className={`!text-end font-semibold ${lot.quantityOnHand < 0 ? "text-danger" : ""}`}>
                  {lot.quantityOnHand}
                </td>
                <td className="text-[#8c9097] dark:text-white/50">{lot.purchaseBatchRef ?? ""}</td>
                <td>
                  <Badge tone={lot.ownerKind === "company" ? "primary" : "secondary"}>{lot.ownerName}</Badge>
                </td>
                <td onClick={(event) => event.stopPropagation()}>
                  {/* Where it is, picked here (D, 14 October 2026). The whole batch moves; part of one is moved from its panel. */}
                  {canManage && lot.quantityOnHand > 0 ? (
                    <select
                      aria-label={`Where ${lot.slabName ?? lot.productName ?? "this"} is`}
                      className={`form-select !py-1 !ps-2 !pe-7 !text-[0.8125rem] min-w-[9rem] ${lot.locationId ? "" : "text-[#8c9097]"}`}
                      value={lot.locationId ?? ""}
                      onChange={(event) => {
                        const to = event.target.value;
                        if (!to || to === lot.locationId) return;
                        void api
                          .moveLot(lot.id, { quantity: lot.quantityOnHand, toLocationId: to, happenedOn: singaporeToday() })
                          .then(() => {
                            setNotice(`${lot.slabName ?? lot.productName ?? "It"} is now at ${locations.find((place) => place.id === to)?.name ?? "the new place"}.`);
                            void reload();
                          })
                          .catch((problem: unknown) => setError(problem instanceof ApiError ? problem.message : "Could not set the location"));
                      }}
                    >
                      {lot.locationId ? null : <option value="">No location set</option>}
                      {locations
                        .filter((place) => place.isActive || place.id === lot.locationId)
                        .map((place) => (
                          <option key={place.id} value={place.id}>
                            {place.name}
                          </option>
                        ))}
                    </select>
                  ) : (
                    lot.locationName ?? <span className="text-[#8c9097] dark:text-white/50">No location set</span>
                  )}
                </td>
                {seesCost ? (
                  <td className="!text-end">
                    {lot.isFree ? <Badge tone="info">Free</Badge> : formatMoney(lot.totalCostCents ?? 0)}
                  </td>
                ) : null}
                {seesCost ? (
                  <td className="!text-end text-[#8c9097] dark:text-white/50">
                    {lot.quantityOnHand > 0 && !lot.isFree ? formatMoney(Math.round(lot.averageCostCents ?? 0)) : ""}
                  </td>
                ) : null}
                <td>
                  <div className="flex items-center flex-wrap gap-1">
                    {!lot.isComplete ? <Badge tone="warning">Incomplete</Badge> : null}
                    {lot.isVerified ? <Badge tone="success">Verified</Badge> : null}
                    {lot.isComplete && !lot.isVerified ? <Badge tone="light">Not verified</Badge> : null}
                    {lot.isVerified && lot.verifiedByName ? (
                      <span className="text-[0.6875rem] text-[#8c9097] dark:text-white/50">
                        by {lot.verifiedByName}
                        {lot.verifiedAt ? " on " + singaporeDay(lot.verifiedAt) : ""}
                      </span>
                    ) : null}
                  </div>
                </td>
                <td>
                  <Actions>
                    <IconAction
                      icon={lot.isComplete ? "ri-eye-line" : "ri-edit-line"}
                      label={lot.isComplete ? "Open: count, move and history" : "Finish the details"}
                      tip={lot.isComplete ? "Open batch: count, transfer and history" : "Complete product details"}
                      tone={lot.isComplete ? "info" : "warning"}
                      onClick={() => setOpenLotId(lot.id)}
                    />
                    {canCheck && lot.isComplete && !lot.isVerified ? (
                      <IconAction
                        icon="ri-check-double-line"
                        label="Checked against the shelf, and right"
                        tip="Mark as verified"
                        tone="success"
                        onClick={() => void check(lot)}
                      />
                    ) : null}
                  </Actions>
                </td>
              </tr>
            ))}
          </Table>
        )}
        <Pager total={lots.length} page={paged.page} pages={paged.pages} onPage={paged.setPage} />
      </Box>

      {canManage || canSetPrice ? (
        <ProductList
          products={products}
          locations={locations}
          canAdjust={canAdjust}
          priceOnly={!canManage}
          onChanged={() => void reload()}
        />
      ) : null}

      {openLotId ? (
        <StockPanel
          lotId={openLotId}
          seesCost={seesCost}
          canManage={canManage}
          canAdjust={canAdjust}
          canBuyFromStaff={canBuyFromStaff}
          products={products}
          locations={locations}
          onChanged={() => void reload()}
          onClose={() => setOpenLotId(undefined)}
        />
      ) : null}

      {modal === "product" ? <AddProduct products={products} onClose={closeModal} onDone={afterSave} /> : null}
      <AddLocation open={modal === "location"} locations={locations} onClose={closeModal} onDone={afterSave} />
      {modal === "import" ? (
        <StockImport
          locations={locations}
          onClose={closeModal}
          onDone={(message) => {
            closeModal();
            afterSave(message);
          }}
        />
      ) : null}
    </>
  );
}

/** What OHC sell today. Anything else is typed in, so a new game is not a code change. */
const GAMES = ["Pokemon", "Lorcana", "Riftbound", "One Piece", "Yu-Gi-Oh"];

/** A word as it is compared rather than written: Pokemon and Pokémon are one game. */
const fold = (text: string): string =>
  text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

/**
 * The spellings the shop's own products use for a thing, the most used first, and after
 * them any of `also` the shop has not got yet. A product added by hand is offered these, so
 * it lands on the tab that is already on the booth screen instead of starting another.
 */
function inUse(products: readonly Product[], of: (product: Product) => string | null, also: readonly string[] = []): string[] {
  const counts = new Map<string, number>();
  for (const product of products) {
    const value = of(product)?.trim();
    if (value && product.isActive) counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  const used = [...counts].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([value]) => value);
  // Lorcana is not offered beside Disney Lorcana: either way round, one is the other.
  return [...used, ...also.filter((one) => !used.some((value) => fold(value).includes(fold(one)) || fold(one).includes(fold(value))))];
}

/** The product list, each price shown with how old it is (answer 143). */
/**
 * The product's picture, larger while the pointer rests on it (D, 6 October 2026), and
 * opened in a tab of its own when tapped, which is how an iPad with no pointer sees it.
 * The larger one floats over the page, since the table would clip it.
 */
function PhotoPeek({ photoId, cover = null }: { readonly photoId: string; readonly cover?: CertBox | null }): React.ReactElement {
  const [at, setAt] = useState<{ left: number; top: number } | undefined>();
  // A slab whose cert number is hidden shows, opens and saves with it covered (D, 9 October 2026).
  const src = useCoveredPhoto(`/api/photos/${photoId}`, cover);
  if (!src) return <span className="shrink-0 w-9 h-9 rounded-sm border border-defaultborder bg-light" />;
  return (
    <a
      href={src}
      target="_blank"
      rel="noreferrer"
      className="shrink-0"
      onMouseEnter={(event) => {
        const box = event.currentTarget.getBoundingClientRect();
        setAt({ left: box.right + 12, top: Math.max(8, Math.min(box.top - 100, window.innerHeight - 268)) });
      }}
      onMouseLeave={() => setAt(undefined)}
    >
      <img src={src} alt="" className="w-9 h-9 rounded-sm object-cover border border-defaultborder" />
      {at ? (
        <img
          src={src}
          alt=""
          className="fixed z-[200] w-[15rem] h-[15rem] object-contain rounded-md border border-defaultborder bg-white dark:bg-bodybg shadow-lg pointer-events-none"
          style={{ left: at.left, top: at.top }}
        />
      ) : null}
    </a>
  );
}

/** A product's selling price on its own, for a supervisor (D, 8 October 2026). */
function SetPrice({
  product,
  onClose,
  onSaved,
}: {
  readonly product: Product;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}): React.ReactElement {
  const was = product.defaultPriceCents === null ? "" : formatMoney(product.defaultPriceCents).replace(/,/g, "");
  const [price, setPrice] = useState(was);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function save(): Promise<void> {
    setBusy(true);
    setError(undefined);
    try {
      await api.setProductPrice(product.id, price.trim());
      onSaved();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "The price could not be saved");
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title={`Change price - ${product.name}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ti-btn ti-btn-light" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="ti-btn ti-btn-primary-full" disabled={busy || !price.trim() || price.trim() === was} onClick={() => void save()}>
            {busy ? <Working label="Saving" /> : "Save price"}
          </button>
        </>
      }
    >
      {error ? <Alert tone="danger">{error}</Alert> : null}
      <Field id="set-price" label="Selling price">
        <input id="set-price" type="text" inputMode="decimal" className="form-control" value={price} onChange={(event) => setPrice(event.target.value)} />
      </Field>
    </Modal>
  );
}

function ProductList({
  products,
  locations,
  canAdjust,
  priceOnly = false,
  onChanged,
}: {
  readonly products: readonly Product[];
  readonly locations: readonly Location[];
  readonly canAdjust: boolean;
  /** A supervisor: each product's selling price can be changed, and nothing more. */
  readonly priceOnly?: boolean;
  readonly onChanged: () => void;
}): React.ReactElement {
  const [pricing, setPricing] = useState<Product | undefined>();
  const canRead = useReading();
  const [matchingPictures, setMatchingPictures] = useState(false);
  const unfinished = useMemo(() => products.filter((product) => !product.isComplete).length, [products]);
  /**
   * A hundred products is five pages, and the one to rename or keep back is on none of the
   * first: every word typed has to be somewhere in its name, code, game, set, section or
   * category, so "151 chinese" finds the Chinese 151 things.
   */
  const [find, setFind] = useState("");
  const found = useMemo(() => {
    const words = find.toLowerCase().split(/\s+/).filter(Boolean);
    if (words.length === 0) return products;
    return products.filter((product) => {
      const text = [
        product.name,
        productName(product),
        productCode(product),
        product.game,
        product.setName,
        product.section,
        product.category,
        product.language,
        product.notForSale ? "not available for sale" : "",
      ]
        .join(" ")
        .toLowerCase();
      return words.every((word) => text.includes(word));
    });
  }, [products, find]);
  const paged = usePaged(found);
  const [editing, setEditing] = useState<Product | undefined>();
  const [historyProduct, setHistoryProduct] = useState<Product | undefined>();
  /** What a product is: its contents worked out, and a description (D, 12 October 2026). */
  const [aboutProduct, setAboutProduct] = useState<Product | undefined>();
  const [reviewUndo, setReviewUndo] = useState(false);
  const [lastChanged, setLastChanged] = useState<Product>();
  const [receiving, setReceiving] = useState<Product | undefined>();
  const [problem, setProblem] = useState<string | undefined>();

  useEffect(() => {
    const review = (event: KeyboardEvent): void => {
      if (editing || receiving || historyProduct || matchingPictures || !lastChanged) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest("input, textarea, [contenteditable=true], [role=dialog]")) return;
      if ((event.ctrlKey || event.metaKey) && !event.shiftKey && event.key.toLowerCase() === "z") { event.preventDefault(); setReviewUndo(true); setHistoryProduct(lastChanged); }
    };
    window.addEventListener("keydown", review); return () => window.removeEventListener("keydown", review);
  }, [editing, receiving, historyProduct, matchingPictures, lastChanged]);

  /**
   * On the list or off it. A product is never deleted: old sales name it, and a row that
   * disappears takes the meaning of those with it.
   */
  async function pin(product: Product, pinned: boolean): Promise<void> {
    try {
      await api.pinProduct(product.id, pinned);
      setLastChanged(product);
      setProblem(undefined);
      onChanged();
    } catch (trouble) {
      setProblem(trouble instanceof ApiError ? trouble.message : "Could not update the product");
    }
  }

  async function setActive(product: Product, active: boolean): Promise<void> {
    try {
      await api.setProductActive(product.id, active);
      setLastChanged(product);
      setProblem(undefined);
      onChanged();
    } catch (trouble) {
      setProblem(trouble instanceof ApiError ? trouble.message : "Could not update the product");
    }
  }

  return (
    <Box title="Product catalogue" count={products.length} actions={<>
      {unfinished ? <Badge tone="warning">{unfinished} incomplete</Badge> : null}
      {/* The whole catalogue as a file, to line up against an older sheet (D, 12 October 2026). */}
      <button type="button" className="ti-btn ti-btn-light !mb-0" onClick={() => downloadCatalogue(products)}><i className="ri-download-2-line" /> Download CSV</button>
      {canRead && products.some((product) => product.isActive) ? <button type="button" className="ti-btn ti-btn-light !mb-0" onClick={() => setMatchingPictures(true)}><i className="ri-image-add-line" /> Match product pictures</button> : null}
    </>}>
      {matchingPictures ? <ProductPictures products={products} onClose={() => setMatchingPictures(false)} onChanged={onChanged} /> : null}
      {problem ? <Alert tone="danger">{problem}</Alert> : null}
      {products.length > 0 ? (
        <div className="grid grid-cols-12 gap-4 mb-4">
          <div className="xl:col-span-4 col-span-12">
            <div className="input-group">
              <span className="input-group-text">
                <i className="ri-search-line" />
              </span>
              <input
                id="product-search"
                type="text"
                className="form-control"
                value={find}
                placeholder="Search by name, product code, game or subcategory"
                aria-label="Find a product"
                onChange={(event) => setFind(event.target.value)}
              />
            </div>
          </div>
        </div>
      ) : null}
      {products.length === 0 ? (
        <Empty icon="ri-price-tag-3-line">No products yet.</Empty>
      ) : found.length === 0 ? (
        <Empty icon="ri-price-tag-3-line">No product matches your search.</Empty>
      ) : (
        <Table
          head={
            <>
              <Th>Product</Th>
              <Th end>Quantity on hand</Th>
              <Th end>Sold</Th>
              <Th>Code</Th>
              <Th>Type</Th>
              <Th end>Packs per unit</Th>
              <Th end>Price</Th>
              <Th>Price last updated</Th>
              <Th end>Action</Th>
            </>
          }
        >
          {paged.rows.map((product) => (
            <tr key={product.id} className="border-b border-defaultborder">
              <td>
                <div className="flex items-center gap-2">
                {product.photoId ? <PhotoPeek photoId={product.photoId} /> : null}
                <TwoLine
                  main={
                    product.isActive ? (
                      product.notForSale ? (
                        <span>
                          {productName(product)} <Badge tone="warning">Not available for sale</Badge>
                        </span>
                      ) : (
                        productName(product)
                      )
                    ) : (
                      <span className="text-[#8c9097] dark:text-white/50">
                        {productName(product)} <Badge tone="light">Archived</Badge>
                      </span>
                    )
                  }
                  sub={[product.game, product.section, product.setName, product.language].filter(Boolean).join(" · ") || product.category}
                />
                {product.pinned ? <i className="ri-pushpin-fill text-primary" title="Pinned to the top of its group on the iPads" /> : null}
                </div>
              </td>
              <td className="!text-end font-semibold">
                {product.tracksStock ? (
                  product.onHand
                ) : (
                  <span className="text-[#8c9097] dark:text-white/50 font-normal text-[0.75rem] whitespace-nowrap" title="Quantity not tracked: sales of it take nothing from inventory">
                    Not counted
                  </span>
                )}
              </td>
              {/* Sold, and where (D, 3 November 2026: how many went on TikTok of what we own). */}
              <td className="!text-end whitespace-nowrap">
                {(product.soldTikTok ?? 0) + (product.soldElsewhere ?? 0) > 0 ? (
                  <TwoLine
                    main={String((product.soldTikTok ?? 0) + (product.soldElsewhere ?? 0))}
                    sub={`TikTok ${product.soldTikTok ?? 0} · shop and booth ${product.soldElsewhere ?? 0}`}
                  />
                ) : (
                  <span className="text-[#8c9097] dark:text-white/50">0</span>
                )}
              </td>
              <td>{productCode(product)}</td>
              <td className="text-[#8c9097] dark:text-white/50">{product.itemType.toUpperCase()}</td>
              <td className="!text-end">
                {product.packsPerBox ??
                  (["box", "etb", "bundle"].includes(product.itemType) ? <Badge tone="warning">Required</Badge> : "")}
              </td>
              <td className="!text-end">
                {product.defaultPriceCents === null ? (
                  ""
                ) : product.defaultPriceCents === 0 ? (
                  <Badge tone="success">Complimentary item</Badge>
                ) : (
                  formatMoney(product.defaultPriceCents)
                )}
                {product.averageCostCents === null ? null : (
                  <CostCard productId={product.id} note={product.averageCostNote ?? null} costCents={product.averageCostCents}>
                    <div
                      className={`text-[0.6875rem] ${
                        product.defaultPriceCents !== null && product.defaultPriceCents < product.averageCostCents
                          ? "text-danger font-semibold"
                          : "text-[#8c9097] dark:text-white/50"
                      }`}
                    >
                      cost {formatMoney(product.averageCostCents)}
                    </div>
                  </CostCard>
                )}
                {/* The average over the batches on hand (D, 8 October 2026). */}
                {product.stockAverageCostCents !== null && product.stockAverageCostCents !== undefined ? (
                  <CostCard productId={product.id} note={null} costCents={Math.round(product.stockAverageCostCents)} onHand={product.stockAverageOver ?? 0}>
                    <div
                      className={`text-[0.6875rem] ${
                        product.defaultPriceCents !== null && product.defaultPriceCents < product.stockAverageCostCents
                          ? "text-danger font-semibold"
                          : "text-[#8c9097] dark:text-white/50"
                      }`}
                    >
                      avg cost {formatMoney(Math.round(product.stockAverageCostCents))} on {product.stockAverageOver}
                    </div>
                  </CostCard>
                ) : null}
              </td>
              <td className="text-[#8c9097] dark:text-white/50">
                {product.defaultPriceCents === null ? "" : ageOf(product.priceSetOn)}
              </td>
              <td>
                <Actions>
                  {canAdjust ? (
                    <IconAction
                      icon="ri-add-box-line"
                      label="Put stock on hand"
                      tip="Add inventory quantity"
                      tone="success"
                      onClick={() => setReceiving(product)}
                    />
                  ) : null}
                  {canAdjust && product.isActive ? (
                    <IconAction
                      icon={product.pinned ? "ri-unpin-line" : "ri-pushpin-line"}
                      label={product.pinned ? "Let it find its own place" : "Keep it at the top on the booth screen"}
                      tip={product.pinned ? "Unpin product" : "Pin product"}
                      tone="primary"
                      onClick={() => void pin(product, !product.pinned)}
                    />
                  ) : null}
                  {priceOnly ? (
                    <IconAction icon="ri-price-tag-3-line" label="Change price" tip="Change selling price" tone="info" onClick={() => setPricing(product)} />
                  ) : (
                    <>
                      <IconAction
                        icon="ri-pencil-line"
                        label={product.isComplete ? "Edit" : "Finish the details"}
                        tip={product.isComplete ? "Edit product" : "Complete product details"}
                        tone={product.isComplete ? "info" : "warning"}
                        onClick={() => setEditing(product)}
                      />
                      <IconAction icon="ri-information-line" label="What this product is" tip="What it is and what is inside" tone="info" onClick={() => setAboutProduct(product)} />
                      <IconAction icon="ri-history-line" label="Product change history" tip="View changes and undo" tone="primary" onClick={() => { setReviewUndo(false); setHistoryProduct(product); }} />
                    </>
                  )}
                  {canAdjust ? (
                    product.isActive ? (
                      <IconAction
                        icon="ri-delete-bin-line"
                        label="Take it off the list"
                        tip="Archive product"
                        tone="danger"
                        onClick={() => void setActive(product, false)}
                      />
                    ) : (
                      <IconAction
                        icon="ri-arrow-go-back-line"
                        label="Put it back on the list"
                        tip="Restore product"
                        tone="success"
                        onClick={() => void setActive(product, true)}
                      />
                    )
                  ) : null}
                </Actions>
              </td>
            </tr>
          ))}
        </Table>
      )}
      <Pager total={found.length} page={paged.page} pages={paged.pages} onPage={paged.setPage} />
      {pricing ? (
        <SetPrice
          product={pricing}
          onClose={() => setPricing(undefined)}
          onSaved={() => {
            setPricing(undefined);
            onChanged();
          }}
        />
      ) : null}
      {lastChanged ? <p className="text-sm mb-3">Product saved. Ctrl+Z opens its latest change for review.</p> : null}
      {aboutProduct ? (
        <AboutProduct
          product={products.find((one) => one.id === aboutProduct.id) ?? aboutProduct}
          products={products}
          canWrite={canRead}
          onClose={() => setAboutProduct(undefined)}
          onChanged={onChanged}
        />
      ) : null}
      {historyProduct ? <ProductHistory product={historyProduct} reviewUndo={reviewUndo} onClose={() => setHistoryProduct(undefined)} onChanged={onChanged} /> : null}
      {editing ? (
        <EditProduct
          product={editing}
          products={products}
          onClose={() => setEditing(undefined)}
          onDone={() => {
            setLastChanged(editing);
            setEditing(undefined);
            onChanged();
          }}
        />
      ) : null}
      {receiving ? (
        <ReceiveStock
          product={receiving}
          locations={locations}
          onClose={() => setReceiving(undefined)}
          onDone={() => {
            setReceiving(undefined);
            onChanged();
          }}
        />
      ) : null}
    </Box>
  );
}

/**
 * Putting stock on hand for a product that is already in the list.
 *
 * Stock that was bought should come in through a purchase, so what was paid stays
 * attached to what arrived. This is for the rest: what was already on the shelf when the
 * system started, and what somebody is holding at a show and needs on the iPad now.
 */
function ProductHistory({ product, reviewUndo, onClose, onChanged }: { readonly product: Product; readonly reviewUndo: boolean; readonly onClose: () => void; readonly onChanged: () => void }): React.ReactElement {
  const [entries, setEntries] = useState<Awaited<ReturnType<typeof api.productHistory>>["entries"]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string>();
  const [reason, setReason] = useState("");
  const [selected, setSelected] = useState<string>();
  useEffect(() => { let mounted = true; void api.productHistory(product.id).then((result) => { if (mounted) { setEntries(result.entries); if (reviewUndo && result.entries[0]?.canUndo) setSelected(result.entries[0].id); } }).catch((error: unknown) => { if (mounted) setProblem(error instanceof Error ? error.message : "Could not load history"); }).finally(() => { if (mounted) setLoading(false); }); return () => { mounted = false; }; }, [product.id, reviewUndo]);
  useEffect(() => {
    const review = (event: KeyboardEvent): void => {
      const target = event.target as HTMLElement | null;
      if (target?.closest("input, textarea, [contenteditable=true]")) return;
      if ((event.ctrlKey || event.metaKey) && !event.shiftKey && event.key.toLowerCase() === "z" && entries[0]?.canUndo && !busy) { event.preventDefault(); setSelected(entries[0].id); }
    };
    window.addEventListener("keydown", review); return () => window.removeEventListener("keydown", review);
  }, [entries, busy]);
  const labels: Record<string, string> = { name: "Product name", category: "Category", game: "Game", set_name: "Card set", language: "Language", packs_per_box: "Packs per unit", section: "Subcategory", not_for_sale: "Not available for sale", tracks_stock: "Quantity tracking", is_active: "Active", pinned: "Pinned", default_price_cents: "Price S$", price_set_on: "Price date" };
  const value = (key: string, one: unknown): string => one === null || one === undefined ? "Blank" : key === "default_price_cents" ? formatMoney(Number(one)) : typeof one === "boolean" ? one ? "Yes" : "No" : String(one);
  async function undo(): Promise<void> {
    if (!selected || !reason.trim() || busy) return;
    setBusy(true); setProblem(undefined);
    try { await api.undoProductChange(product.id, selected, reason); setEntries((await api.productHistory(product.id)).entries); setSelected(undefined); setReason(""); onChanged(); }
    catch (error) { setProblem(error instanceof Error ? error.message : "Could not undo the change"); }
    finally { setBusy(false); }
  }
  return <Modal open title={`Product history - ${productCode(product)}`} onClose={onClose} footer={<button type="button" className="ti-btn ti-btn-light" disabled={busy} onClick={onClose}>Close</button>}>
    <p className="mb-4">Review saved changes before undoing them. Ctrl+Z opens the latest change for review here. Product details and prices can be restored; stock quantities, purchases and payments are kept separately.</p>
    {problem ? <Alert tone="danger">{problem}</Alert> : null}
    {loading ? <p>Loading history...</p> : entries.length === 0 ? <p>No saved changes.</p> : entries.map((entry) => <div key={entry.id} className="border border-defaultborder rounded p-3 mb-3">
      <p className="font-semibold">{new Date(entry.at).toLocaleString("en-SG", { timeZone: "Asia/Singapore" })} {entry.actorName ?? "System"} ({entry.action})</p>
      {entry.reason ? <p className="mb-2">{entry.reason}</p> : null}
      <Table head={<><Th>Field</Th><Th>Before</Th><Th>After</Th></>}>{Object.keys(entry.after ?? entry.before ?? {}).map((key) => <tr key={key}><td>{labels[key] ?? key.replaceAll("_", " ")}</td><td>{value(key, entry.before?.[key])}</td><td>{value(key, entry.after?.[key])}</td></tr>)}</Table>
      {entry.canUndo ? <button type="button" className="ti-btn ti-btn-light mt-2" disabled={busy} onClick={() => setSelected(entry.id)}>Review undo</button> : null}
      {selected === entry.id ? <div className="mt-3"><p className="mb-2">This restores the Before values shown above.</p><Field id="product-undo-reason" label="Reason for undo"><input id="product-undo-reason" className="form-control" maxLength={500} value={reason} onChange={(event) => setReason(event.target.value)} /></Field><button type="button" className="ti-btn ti-btn-danger-full mt-2" disabled={busy || !reason.trim()} onClick={() => void undo()}>Undo saved change</button></div> : null}
    </div>)}
  </Modal>;
}

function ReceiveStock({
  product,
  locations,
  onClose,
  onDone,
}: {
  readonly product: Product;
  readonly locations: readonly Location[];
  readonly onClose: () => void;
  readonly onDone: () => void;
}): React.ReactElement {
  const [quantity, setQuantity] = useState("1");
  const [unitCost, setUnitCost] = useState("");
  const [locationId, setLocationId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function submit(event?: FormEvent): Promise<void> {
    event?.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      await api.receiveStock({
        productId: product.id,
        quantity: Number(quantity),
        unitCost: unitCost.trim() === "" ? null : unitCost.trim(),
        locationId: locationId || null,
      });
      onDone();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not add the inventory quantity");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title={`Add inventory quantity - ${productName(product)}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ti-btn ti-btn-light" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="ti-btn ti-btn-primary-full"
            disabled={busy || !(Number(quantity) > 0)}
            onClick={() => void submit()}
          >
            {busy ? <Working label="Saving" /> : "Add inventory quantity"}
          </button>
        </>
      }
    >
      <form className="grid grid-cols-12 gap-4" onSubmit={(event) => void submit(event)}>
        {error ? (
          <div className="col-span-12">
            <Alert tone="danger">{error}</Alert>
          </div>
        ) : null}
        {product.tracksStock ? null : (
          <div className="col-span-12">
            <Alert tone="info">
              This product&apos;s quantity is not counted yet. Adding a quantity starts counting it: every sale of it, at the booth, in the shop or on TikTok,
              then takes from this quantity.
            </Alert>
          </div>
        )}
        <Field id="receive-quantity" label="Quantity" className="sm:col-span-6 col-span-12">
          <input
            id="receive-quantity"
            type="number"
            min={1}
            className="form-control"
            value={quantity}
            onChange={(changed) => setQuantity(changed.target.value)}
          />
        </Field>
        <Field
          id="receive-cost"
          label="Unit cost"
          hint="Leave blank if the cost is not yet known"
          className="sm:col-span-6 col-span-12"
        >
          <input
            id="receive-cost"
            type="text"
            inputMode="decimal"
            className="form-control"
            value={unitCost}
            onChange={(changed) => setUnitCost(changed.target.value)}
          />
        </Field>
        <div className="col-span-12">
          <SelectField
            id="receive-place"
            label="Inventory location"
            value={locationId}
            placeholder="No specific location"
            options={locations.map((place) => ({ value: place.id, label: place.name }))}
            onChange={setLocationId}
          />
        </div>
        <div className="col-span-12">
          <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mb-0">
            Use this for inventory that has been counted, not purchased. Record anything bought from a supplier as a
            supplier purchase so the cost and payment stay together.
          </p>
        </div>
      </form>
    </Modal>
  );
}

/** A product's details and its price. The code and the type stay: they are on shelves and in old records. */
function EditProduct({
  product,
  products,
  onClose,
  onDone,
}: {
  readonly product: Product;
  /** Every product, for the games, categories and sections already in use. */
  readonly products: readonly Product[];
  readonly onClose: () => void;
  readonly onDone: () => void;
}): React.ReactElement {
  const canFindPicture = useReading();
  const [findingPicture, setFindingPicture] = useState(false);
  const games = useMemo(() => inUse(products, (one) => one.game, GAMES), [products]);
  const categories = useMemo(() => inUse(products, (one) => one.category), [products]);
  const sections = useMemo(() => inUse(products, (one) => one.section, SHELF_SECTIONS), [products]);
  const [name, setName] = useState(product.name);
  const [category, setCategory] = useState(product.category);
  const [game, setGame] = useState(product.game ?? "");
  const [series, setSeries] = useState(product.setName ?? "");
  const [language, setLanguage] = useState(product.language ?? "");
  const [packs, setPacks] = useState(product.packsPerBox ? String(product.packsPerBox) : "");
  const [insideId, setInsideId] = useState(product.containsProductId ?? "");
  const [insideCount, setInsideCount] = useState(product.containsQuantity ? String(product.containsQuantity) : "");
  /** What it is can be put right here: a display that came in as "other" (D, 13 October 2026). */
  const [itemType, setItemType] = useState<ItemType>(product.itemType);
  const [section, setSection] = useState(product.section ?? "");
  const [held, setHeld] = useState(product.notForSale);
  const [uncounted, setUncounted] = useState(!product.tracksStock);
  const [price, setPrice] = useState(product.defaultPriceCents === null ? "" : formatMoney(product.defaultPriceCents).replace(/,/g, ""));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  /** A new picture, shrunk and ready, shown before it is saved. */
  const [picture, setPicture] = useState<{ contentType: string; data: string; preview: string } | undefined>();
  const boxLike = ["box", "etb", "bundle"].includes(itemType);
  const oldPrice = product.defaultPriceCents === null ? "" : formatMoney(product.defaultPriceCents).replace(/,/g, "");

  async function submit(event?: FormEvent): Promise<void> {
    event?.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      // The type first: what may be saved below, packs per unit say, depends on it.
      if (itemType !== product.itemType) await api.setProductType(product.id, itemType);
      await api.updateProduct(product.id, {
        name,
        category,
        game: game || null,
        setName: series || null,
        language: language || null,
        packsPerBox: boxLike && packs.trim() ? Number(packs) : null,
        section: section.trim() || null,
        notForSale: held,
        tracksStock: !uncounted,
      });
      if ((insideId || "") !== (product.containsProductId ?? "") || (Number(insideCount) || 0) !== (product.containsQuantity ?? 0)) {
        await api.setProductContents(product.id, insideId && Number(insideCount) ? insideId : null, insideId && Number(insideCount) ? Number(insideCount) : null);
      }
      // A price is dated when it changes, so it is only sent when it did.
      if (price.trim() && price.trim() !== oldPrice) await api.setProductPrice(product.id, price.trim());
      if (picture) await api.setProductPicture(product.id, picture.contentType, picture.data);
      onDone();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not save the product");
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title={`Edit product - ${productCode(product)}, ${typeOf(product.itemType).label}`}
      onClose={onClose}
      footer={<ModalActions busy={busy} label="Save changes" onCancel={onClose} onSave={() => void submit()} />}
    >
      <form className="grid grid-cols-12 gap-4" onSubmit={(event) => void submit(event)}>
        {error ? (
          <div className="col-span-12">
            <Alert tone="danger">{error}</Alert>
          </div>
        ) : null}
        <Field id="e-name" label="Product name" className="col-span-12">
          <input id="e-name" type="text" className="form-control" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field
          id="e-game"
          label="Game"
          hint="Shown as the top row on the selling screen. A product without a game appears under Other."
          className="sm:col-span-6 col-span-12"
        >
          <ChoiceField id="e-game" value={game} options={games} placeholder="Pokémon" onChange={setGame} />
        </Field>
        <Field id="e-category" label="Product category" className="sm:col-span-6 col-span-12">
          <ChoiceField id="e-category" value={category} options={categories} onChange={setCategory} />
        </Field>
        <Field
          id="e-section"
          label="Subcategory"
          hint="Groups the product within its category on the selling screen."
          className="sm:col-span-6 col-span-12"
        >
          <ChoiceField id="e-section" value={section} options={sections} placeholder="Booster Packs" onChange={setSection} />
        </Field>
        <Field id="e-set" label="Card set" className="sm:col-span-6 col-span-12">
          <input id="e-set" type="text" className="form-control" value={series} onChange={(e) => setSeries(e.target.value)} />
        </Field>
        <Field id="e-language" label="Language" className="sm:col-span-4 col-span-12">
          <input id="e-language" type="text" className="form-control" value={language} placeholder="EN" onChange={(e) => setLanguage(e.target.value)} />
        </Field>
        <Field
          id="e-packs"
          label="Packs per unit"
          className="sm:col-span-4 col-span-12"
          hint={boxLike ? "Required before the unit can be opened into packs." : "Only for a box, Elite Trainer Box or bundle."}
        >
          <input
            id="e-packs"
            type="text"
            inputMode="numeric"
            className="form-control"
            value={boxLike ? packs : ""}
            disabled={!boxLike}
            onChange={(e) => setPacks(e.target.value)}
          />
        </Field>
        <Field id="e-type" label="Product type" className="col-span-12" hint="What the product is. Change it here if it came in as Other Product.">
          <SelectField<ItemType>
            id="e-type"
            value={itemType}
            options={PRODUCT_TYPES.map((type) => ({ value: type.value, label: type.label }))}
            onChange={(choice) => {
              setItemType(choice);
              if (typeOf(choice).section && !section) setSection(typeOf(choice).section);
            }}
          />
        </Field>
        {holdsThings(itemType) ? (
          <>
            <ContentsFields
              id="e"
              itemType={itemType}
              products={products.filter((one) => one.id !== product.id)}
              insideId={insideId}
              insideCount={insideCount}
              onInside={setInsideId}
              onCount={setInsideCount}
            />
            <div className="col-span-12">
              <p className="text-[0.813rem] rounded-md bg-light dark:bg-bodybg px-3 py-2 mb-0">
                {contentsSentence(
                  itemType,
                  { containsProductId: insideId || null, containsQuantity: Number(insideCount) || null, packsPerBox: boxLike && packs.trim() ? Number(packs) : null },
                  products,
                )}
              </p>
            </div>
          </>
        ) : null}
        <Field
          id="e-price"
          label="Selling price"
          className="sm:col-span-4 col-span-12"
          hint={`${product.priceSetOn ? `Last updated ${ageOf(product.priceSetOn)}.` : "Dated when saved."} A price of 0 makes it a complimentary item.`}
        >
          <input id="e-price" type="text" inputMode="decimal" className="form-control" value={price} onChange={(e) => setPrice(e.target.value)} />
        </Field>
        <div className="col-span-12">
          <label className="flex items-center gap-2 text-[0.813rem]">
            <input
              id="e-uncounted"
              type="checkbox"
              className="form-check-input"
              checked={uncounted}
              disabled={product.onHand !== 0}
              onChange={(e) => setUncounted(e.target.checked)}
            />
            Do not track inventory
          </label>
          <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-1 mb-0">
            {product.onHand !== 0
              ? `This product has ${product.onHand} on hand, so quantity tracking stays on until that quantity is counted out or written off.`
              : "Record sales without tracking the remaining quantity of this product."}
          </p>
        </div>
        <div className="col-span-12">
          <label className="flex items-center gap-2 text-[0.813rem]">
            <input
              id="e-not-for-sale"
              type="checkbox"
              className="form-check-input"
              checked={held}
              onChange={(e) => setHeld(e.target.checked)}
            />
            Not available for sale
          </label>
          <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-1 mb-0">
            The product remains in inventory and in the catalogue. The selling screen shows it greyed out and it cannot
            be added to a basket, so it does not need an artificial selling price.
          </p>
        </div>
        <Field
          id="e-picture"
          label="Product image"
          hint="Shown on the product tile on the selling screen. Use a photo of the product or the manufacturer's image."
          className="col-span-12"
        >
          {canFindPicture ? <button type="button" className="ti-btn ti-btn-light mb-2" onClick={() => setFindingPicture(true)}><i className="ri-search-line" /> Find picture online</button> : null}
          {findingPicture ? <FindProductPicture product={product} onClose={() => setFindingPicture(false)} onChosen={(taken) => { setPicture(taken); setFindingPicture(false); }} /> : null}
          <div className="flex items-center gap-3">
            {picture || product.photoId ? (
              <img
                src={picture?.preview ?? `/api/photos/${product.photoId}`}
                alt=""
                className="w-16 h-16 rounded-sm object-cover border border-defaultborder shrink-0"
              />
            ) : null}
            <input
              id="e-picture"
              type="file"
              accept="image/*"
              className="form-control"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                void shrink(file, 480)
                  .then((taken) => setPicture({ contentType: taken.contentType, data: taken.data, preview: taken.preview }))
                  .catch(() => setError("The image could not be read"));
              }}
            />
          </div>
        </Field>
        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </Modal>
  );
}

function AddProduct({
  products,
  onClose,
  onDone,
}: {
  /** What is on the system already: its games, categories and sections are what is offered. */
  readonly products: readonly Product[];
  readonly onClose: () => void;
  readonly onDone: (message?: string) => void;
}): React.ReactElement {
  const games = useMemo(() => inUse(products, (product) => product.game, GAMES), [products]);
  const categories = useMemo(() => inUse(products, (product) => product.category), [products]);
  const sections = useMemo(() => inUse(products, (product) => product.section, SHELF_SECTIONS), [products]);
  // A shop that counts nothing yet, which is OHC before its first stocktake, adds things the
  // same way: sold at their price, the money recorded, no figure for how many are left.
  const active = products.filter((product) => product.isActive);
  const shopCounts = active.length === 0 || active.filter((product) => product.tracksStock).length * 2 > active.length;

  /**
   * A product half filled in and put down (D, 12 October 2026: "save as draft also, so that I
   * can come back later, because I am trying to figure out the product first"). Kept in
   * this browser until the product is saved or the draft is thrown away.
   */
  const [draft] = useState<ProductDraft | undefined>(() => readProductDraft());
  const [fromDraft, setFromDraft] = useState(draft !== undefined);
  const [code, setCode] = useState(draft?.code ?? "");
  const [name, setName] = useState(draft?.name ?? "");
  const [category, setCategory] = useState(draft?.category ?? categories[0] ?? "Booster Packs");
  const [game, setGame] = useState(draft?.game ?? games[0] ?? "Pokemon");
  const [section, setSection] = useState(draft?.section ?? "");
  const [held, setHeld] = useState(draft?.held ?? false);
  const [counted, setCounted] = useState(draft?.counted ?? shopCounts);
  const [series, setSeries] = useState(draft?.series ?? "");
  const [language, setLanguage] = useState(draft?.language ?? "EN");
  const [itemType, setItemType] = useState<ItemType>(draft?.itemType ?? "pack");
  const [packs, setPacks] = useState(draft?.packs ?? "");
  /** What one of these holds: so many of another product (D, 12 October 2026). */
  const [insideId, setInsideId] = useState(draft?.insideId ?? "");
  const [insideCount, setInsideCount] = useState(draft?.insideCount ?? "");
  const [price, setPrice] = useState(draft?.price ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const boxLike = itemType === "box" || itemType === "etb" || itemType === "bundle";
  const holds = { containsProductId: insideId || null, containsQuantity: Number(insideCount) || null, packsPerBox: boxLike && packs ? Number(packs) : null };

  async function submit(event?: FormEvent): Promise<void> {
    event?.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      const added = await api.addProduct({
        code: code.trim() || null,
        name,
        category,
        game: game || null,
        section: section.trim() || null,
        notForSale: held,
        tracksStock: counted,
        setName: series || null,
        language: language || null,
        itemType,
        packsPerBox: boxLike && packs ? Number(packs) : null,
        defaultPrice: price || null,
      });
      if (holds.containsProductId && holds.containsQuantity) {
        await api.setProductContents(added.product.id, holds.containsProductId, holds.containsQuantity);
      }
      clearProductDraft();
      onDone(`${name.trim()} has been added. The iPads receive it within 5 minutes, or immediately when the selling screen is reopened.`);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not add the product");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title="Add product"
      size="lg"
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="ti-btn ti-btn-light me-auto"
            disabled={busy}
            onClick={() => {
              saveProductDraft({ code, name, category, game, section, held, counted, series, language, itemType, packs, insideId, insideCount, price });
              onDone("Draft saved. Open Add product to carry on with it.");
            }}
          >
            <i className="ri-save-line" /> Save as draft
          </button>
          <ModalActions busy={busy} label="Save product" onCancel={onClose} onSave={() => void submit()} />
        </>
      }
    >
      <form className="grid grid-cols-12 gap-4" onSubmit={(event) => void submit(event)}>
        {error ? (
          <div className="col-span-12">
            <Alert tone="danger">{error}</Alert>
          </div>
        ) : null}
        {fromDraft ? (
          <div className="col-span-12">
            <Alert tone="info">
              Carrying on from the draft saved {draft?.savedAt ? new Date(draft.savedAt).toLocaleString("en-SG", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "earlier"}.
              <button
                type="button"
                className="ti-btn ti-btn-light !py-1 !px-2 !text-[0.75rem] !mb-0 ms-2"
                onClick={() => {
                  clearProductDraft();
                  setFromDraft(false);
                  setCode("");
                  setName("");
                  setSection("");
                  setSeries("");
                  setPacks("");
                  setInsideId("");
                  setInsideCount("");
                  setPrice("");
                  setHeld(false);
                }}
              >
                Discard the draft and start again
              </button>
            </Alert>
          </div>
        ) : null}
        <FormPart title="Product details" />
        <Field id="p-name" label="Product name" className="col-span-12">
          <input
            id="p-name"
            type="text"
            className="form-control"
            value={name}
            placeholder="EN Riftbound Origins Booster Box Case"
            autoFocus
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field id="p-type" label="Product type" className="sm:col-span-6 col-span-12" hint="What the product is. It also sets the subcategory below.">
          <SelectField<ItemType>
            id="p-type"
            value={itemType}
            options={PRODUCT_TYPES.map((type) => ({ value: type.value, label: type.label }))}
            onChange={(choice) => {
              setItemType(choice);
              // The group it is shown in follows from what it is; it can still be changed.
              if (typeOf(choice).section) setSection(typeOf(choice).section);
            }}
          />
        </Field>
        <Field
          id="p-code"
          label="Product code"
          className="sm:col-span-6 col-span-12"
          hint="Short and permanent. Leave blank to create one from the product name."
        >
          <input
            id="p-code"
            type="text"
            className="form-control"
            value={code}
            placeholder="EN-ROBC"
            onChange={(e) => setCode(e.target.value.toUpperCase())}
          />
        </Field>
        <Field id="p-game" label="Game" hint="Shown as the top row on the selling screen." className="sm:col-span-6 col-span-12">
          <ChoiceField id="p-game" value={game} options={games} placeholder="Pokémon" onChange={setGame} />
        </Field>
        <Field id="p-set" label="Card set" className="sm:col-span-6 col-span-12">
          <input id="p-set" type="text" className="form-control" value={series} placeholder="Origins" onChange={(e) => setSeries(e.target.value)} />
        </Field>
        <Field id="p-category" label="Product category" className="sm:col-span-4 col-span-12">
          <ChoiceField id="p-category" value={category} options={categories} onChange={setCategory} />
        </Field>
        <Field id="p-section" label="Subcategory" hint="The group it is shown in on the selling screen." className="sm:col-span-4 col-span-12">
          <ChoiceField id="p-section" value={section} options={sections} placeholder="Booster Packs" onChange={setSection} />
        </Field>
        <Field id="p-language" label="Language" className="sm:col-span-4 col-span-12">
          <SelectField
            id="p-language"
            value={language || "other"}
            options={[
              { value: "EN", label: "English" },
              { value: "JP", label: "Japanese" },
              { value: "CN", label: "Chinese" },
              { value: "KR", label: "Korean" },
              { value: "other", label: "Other" },
            ]}
            onChange={(choice) => setLanguage(choice === "other" ? "" : choice)}
          />
        </Field>

        {holdsThings(itemType) ? (
          <>
            <FormPart title="Packaging and contents" />
            <ContentsFields
              id="p"
              itemType={itemType}
              products={products}
              insideId={insideId}
              insideCount={insideCount}
              onInside={setInsideId}
              onCount={setInsideCount}
            />
            {boxLike ? (
              <Field
                id="p-packs"
                label={`Booster packs in one ${typeOf(itemType).unit}`}
                className="sm:col-span-4 col-span-12"
                hint="Needed to open it straight into packs."
              >
                <input id="p-packs" type="text" inputMode="numeric" className="form-control" value={packs} placeholder="24" onChange={(e) => setPacks(e.target.value)} />
              </Field>
            ) : null}
            <div className="col-span-12">
              <p className="text-[0.813rem] rounded-md bg-light dark:bg-bodybg px-3 py-2 mb-0">{contentsSentence(itemType, holds, products)}</p>
            </div>
          </>
        ) : null}

        <FormPart title="Pricing" />
        <Field
          id="p-price"
          label={`Selling price per ${typeOf(itemType).unit}`}
          className="sm:col-span-6 col-span-12"
          hint="Saved with today's date so its age is shown on the selling screen. A price of 0 makes it a complimentary item."
        >
          <input id="p-price" type="text" inputMode="decimal" className="form-control" value={price} placeholder="8.00" onChange={(e) => setPrice(e.target.value)} />
        </Field>
        <div className="sm:col-span-6 col-span-12 text-[0.75rem] text-[#8c9097] dark:text-white/50 self-end pb-1">
          The cost is not typed here. It comes from each purchase, with its supplier and invoice, so different purchases can cost
          different amounts.
        </div>

        <FormPart title="Stock settings" />
        <div className="col-span-12">
          <label className="flex items-center gap-2 text-[0.813rem]">
            <input id="p-counted" type="checkbox" className="form-check-input" checked={counted} onChange={(e) => setCounted(e.target.checked)} />
            Track inventory
          </label>
          <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-1 mb-0">
            On: every purchase adds to the quantity on hand and every sale takes from it. Off: sales are recorded with no count of
            how many are left, as for raw single cards or a price list not counted yet.
          </p>
        </div>
        <div className="col-span-12">
          <label className="flex items-center gap-2 text-[0.813rem]">
            <input id="p-not-for-sale" type="checkbox" className="form-check-input" checked={held} onChange={(e) => setHeld(e.target.checked)} />
            Not available for sale
          </label>
          <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-1 mb-0">
            Remains in the catalogue, is shown greyed out on the selling screen and cannot be added to a basket.
          </p>
        </div>
        {/* Enter in any field saves, as it would on a plain form. */}
        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </Modal>
  );
}

function AddLocation({
  open,
  locations,
  onClose,
  onDone,
}: {
  readonly open: boolean;
  /** The places there are, to rename (D, 14 October 2026). */
  readonly locations: readonly Location[];
  readonly onClose: () => void;
  readonly onDone: () => void;
}): React.ReactElement {
  const [renaming, setRenaming] = useState<{ id: string; name: string } | undefined>();
  const [name, setName] = useState("");
  const [kind, setKind] = useState<Location["kind"]>("shop");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function submit(event?: FormEvent): Promise<void> {
    event?.preventDefault();
    if (!name.trim()) return;
    setError(undefined);
    setBusy(true);
    try {
      await api.addLocation(name, kind);
      setName("");
      onDone();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not add the location");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      title="Locations"
      onClose={onClose}
      footer={
        <ModalActions
          busy={busy}
          disabled={!name.trim()}
          label="Save location"
          onCancel={onClose}
          onSave={() => void submit()}
        />
      }
    >
      <form className="grid grid-cols-12 gap-4" onSubmit={(event) => void submit(event)}>
        {error ? (
          <div className="col-span-12">
            <Alert tone="danger">{error}</Alert>
          </div>
        ) : null}
        <Field id="l-name" label="Location name" className="sm:col-span-7 col-span-12">
          <input
            id="l-name"
            type="text"
            className="form-control"
            value={name}
            placeholder="Marina Main Store"
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field id="l-kind" label="Location type" className="sm:col-span-5 col-span-12">
          <SelectField<Location["kind"]>
            id="l-kind"
            value={kind}
            options={[
              { value: "shop", label: "Shop" },
              { value: "home", label: "Home storage" },
              { value: "show", label: "Event booth" },
              { value: "office", label: "Office" },
              { value: "other", label: "Other" },
            ]}
            onChange={setKind}
          />
        </Field>
        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
      {locations.length > 0 ? (
        <div className="mt-5">
          <p className="form-label mb-2">The places there are</p>
          {locations.map((place) => (
            <div key={place.id} className="flex items-center gap-2 py-1.5 border-b border-defaultborder">
              {renaming?.id === place.id ? (
                <>
                  <input
                    type="text"
                    className="form-control !py-1"
                    aria-label={`New name for ${place.name}`}
                    value={renaming.name}
                    autoFocus
                    onChange={(event) => setRenaming({ id: place.id, name: event.target.value })}
                  />
                  <button
                    type="button"
                    className="ti-btn ti-btn-primary-full !py-1 !px-3 !text-[0.75rem] !mb-0"
                    disabled={busy || !renaming.name.trim()}
                    onClick={() => {
                      setBusy(true);
                      setError(undefined);
                      void api
                        .renameLocation(place.id, renaming.name)
                        .then(() => {
                          setRenaming(undefined);
                          onDone();
                        })
                        .catch((problem: unknown) => setError(problem instanceof ApiError ? problem.message : "Could not rename it"))
                        .finally(() => setBusy(false));
                    }}
                  >
                    Save
                  </button>
                  <button type="button" className="ti-btn ti-btn-light !py-1 !px-3 !text-[0.75rem] !mb-0" onClick={() => setRenaming(undefined)}>
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <span className="flex-1">{place.name}</span>
                  {/* What kind of place: a sale at a tradeshow booth counts as tradeshow sales, one at the shop as retail (D, 15 October 2026). */}
                  <select
                    aria-label={`What kind of place ${place.name} is`}
                    className="form-select !py-1 !ps-2 !pe-7 !text-[0.75rem] !w-auto"
                    value={place.kind}
                    disabled={busy}
                    onChange={(event) => {
                      setBusy(true);
                      setError(undefined);
                      void api
                        .renameLocation(place.id, place.name, event.target.value as Location["kind"])
                        .then(() => onDone())
                        .catch((problem: unknown) => setError(problem instanceof ApiError ? problem.message : "Could not change it"))
                        .finally(() => setBusy(false));
                    }}
                  >
                    <option value="shop">Retail shop</option>
                    <option value="show">Tradeshow booth</option>
                    <option value="office">Office</option>
                    <option value="home">Home</option>
                    <option value="other">Storage or other</option>
                  </select>
                  <button
                    type="button"
                    className="ti-btn ti-btn-light !py-1 !px-3 !text-[0.75rem] !mb-0"
                    onClick={() => setRenaming({ id: place.id, name: place.name })}
                  >
                    <i className="ri-pencil-line" /> Rename
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      ) : null}
    </Modal>
  );
}

/**
 * Every graded card's photo cut close to the slab, and its cert number found and covered,
 * in one go (D, 10 October 2026: "too many slabs already, to be doing manual work"). Each
 * photo is read again, cut to the slab, and kept as the slab's photo; the details are
 * left as they are. A slab whose number cannot be found is left uncovered and counted.
 */
function TidySlabs({ onClose, onDone }: { readonly onClose: () => void; readonly onDone: () => void }): React.ReactElement {
  const [slabs, setSlabs] = useState<Lot[] | undefined>();
  const [running, setRunning] = useState(false);
  const [at, setAt] = useState(0);
  const [result, setResult] = useState<{ covered: number; noNumber: number; failed: string[] } | undefined>();

  useEffect(() => {
    void api.stock({}).then((view) => setSlabs(view.lots.filter((lot) => lot.kind === "slab" && lot.photoId)));
  }, []);

  async function run(): Promise<void> {
    if (!slabs) return;
    setRunning(true);
    let covered = 0;
    let noNumber = 0;
    const failed: string[] = [];
    for (const [index, lot] of slabs.entries()) {
      setAt(index + 1);
      try {
        const { slab } = await api.slab(lot.id);
        const { reading } = await api.readSlab({ photoId: lot.photoId as string });
        let cert = reading.certBox ?? null;
        if (reading.slabBox) {
          const cut = {
            left: Math.max(0, reading.slabBox.left - CROP_MARGIN),
            top: Math.max(0, reading.slabBox.top - CROP_MARGIN),
            right: Math.min(1, reading.slabBox.right + CROP_MARGIN),
            bottom: Math.min(1, reading.slabBox.bottom + CROP_MARGIN),
          };
          // Only worth a new photo when there is something to cut away.
          if (cut.left > 0.01 || cut.top > 0.01 || cut.right < 0.99 || cut.bottom < 0.99) {
            await api.setSlabPhoto(lot.id, await cutPhoto(`/api/photos/${lot.photoId}`, cut));
            cert = boxWithin(cut, cert);
          }
        }
        // Every detail sent back as it is: a save leaves out nothing it does not mean to clear.
        await api.saveSlab(lot.id, {
          cardName: slab?.cardName ?? lot.slabName ?? null,
          certNo: slab?.certNo ?? null,
          grader: slab?.grader ?? null,
          grade: slab?.grade ?? null,
          gradeDescription: slab?.gradeDescription ?? null,
          setName: slab?.setName ?? null,
          yearReleased: slab?.yearReleased ?? null,
          cardNumber: slab?.cardNumber ?? null,
          language: slab?.language ?? null,
          rarity: slab?.rarity ?? null,
          era: slab?.era ?? null,
          labelText: slab?.labelText ?? null,
          sellingPrice: slab?.sellingPriceCents === null || slab?.sellingPriceCents === undefined ? null : (slab.sellingPriceCents / 100).toFixed(2),
          certBox: cert,
          hideCert: cert !== null,
        });
        if (cert) covered += 1;
        else noNumber += 1;
      } catch (problem) {
        failed.push(`${lot.slabName ?? "A graded card"}${lot.certNo ? ` (${lot.certNo})` : ""}: ${problem instanceof ApiError ? problem.message : "could not be done"}`);
      }
    }
    setResult({ covered, noNumber, failed });
    setRunning(false);
    onDone();
  }

  return (
    <Modal
      open
      title="Tidy slab photos"
      onClose={running ? () => undefined : onClose}
      footer={
        result ? (
          <button type="button" className="ti-btn ti-btn-primary-full" onClick={onClose}>
            Close
          </button>
        ) : (
          <>
            <button type="button" className="ti-btn ti-btn-light" disabled={running} onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="ti-btn ti-btn-primary-full" disabled={running || !slabs?.length} onClick={() => void run()}>
              {running ? <Working label={`Working on ${at} of ${slabs?.length ?? 0}`} /> : `Tidy ${slabs?.length ?? 0} slab photos`}
            </button>
          </>
        )
      }
    >
      {slabs === undefined ? (
        <Loading />
      ) : result ? (
        <>
          <p className="mb-2">
            {result.covered} {result.covered === 1 ? "photo" : "photos"} cut close with the cert number covered.
            {result.noNumber > 0 ? ` ${result.noNumber} cut, but the cert number could not be found on the photo: open those and drag across the number.` : ""}
          </p>
          {result.failed.length > 0 ? (
            <Alert tone="warning">
              Not done: {result.failed.join("; ")}
            </Alert>
          ) : null}
        </>
      ) : (
        <p className="text-[#8c9097] dark:text-white/50 mb-0">
          Each of the {slabs.length} graded cards with a photo is read again: the photo is cut close to the slab and the cert number is
          covered. The card details are not changed. The cover can be taken off any slab under Graded card details. Keep this window open
          until it finishes.
        </p>
      )}
    </Modal>
  );
}

/**
 * The cost price on hover, batch by batch: how many at what each, what that came to, and the
 * average they make (D, 10 October 2026: "make the average calculation nicer instead of this
 * formula"). A note that is not batches is shown as it is.
 */
function CostCard({
  productId,
  note,
  costCents,
  onHand,
  children,
}: {
  readonly productId: string;
  /** Set for the average over the batches on hand, rather than the cost price. */
  readonly onHand?: number;
  readonly note: string | null;
  readonly costCents: number;
  readonly children: React.ReactNode;
}): React.ReactElement {
  const [at, setAt] = useState<{ left: number; top: number } | undefined>();
  /** The batches as they were bought, fetched the first time the card opens. */
  const [bought, setBought] = useState<ProductBatch[] | undefined>();
  useEffect(() => {
    if (!at || bought) return;
    void api.productBatches(productId).then((result) => setBought(result.batches)).catch(() => setBought([]));
  }, [at]);
  const rows = note ? batchesOf(note) : [];
  const units = rows.reduce((sum, row) => sum + row.quantity, 0);
  const total = rows.reduce((sum, row) => sum + row.quantity * row.unitCents, 0);
  return (
    <div
      className="cursor-help"
      onMouseEnter={(event) => {
        const box = event.currentTarget.getBoundingClientRect();
        setAt({ left: Math.max(8, Math.min(box.left - 200, window.innerWidth - 500)), top: Math.min(box.bottom + 6, window.innerHeight - 320) });
      }}
      onMouseLeave={() => setAt(undefined)}
    >
      {children}
      {at ? (
        <div
          className="fixed z-[200] w-[30rem] max-w-[95vw] rounded-md border border-defaultborder bg-white dark:bg-bodybg shadow-lg p-3 text-[0.75rem] text-defaulttextcolor pointer-events-none text-start"
          style={{ left: at.left, top: at.top }}
        >
          <p className="font-semibold mb-2">{onHand === undefined ? "How the cost price is worked out" : "Average cost of what is on hand"}</p>
          {rows.length > 0 ? (
            <div className="grid grid-cols-3 gap-x-3 gap-y-0.5">
              <span className="text-[#8c9097] dark:text-white/50">Units</span>
              <span className="text-end text-[#8c9097] dark:text-white/50">Each</span>
              <span className="text-end text-[#8c9097] dark:text-white/50">Cost</span>
              {rows.map((row, index) => (
                <Fragment key={index}>
                  <span>{row.quantity}</span>
                  <span className="text-end">{formatMoney(row.unitCents)}</span>
                  <span className="text-end">{formatMoney(row.quantity * row.unitCents)}</span>
                </Fragment>
              ))}
              <span className="font-semibold border-t border-defaultborder pt-0.5">{units}</span>
              <span className="border-t border-defaultborder" />
              <span className="text-end font-semibold border-t border-defaultborder pt-0.5">{formatMoney(total)}</span>
            </div>
          ) : (
            <p className="mb-0">
              {note
                ? workingOf(note, costCents)
                : onHand === undefined
                  ? "The cost price set for one unit."
                  : `The cost of the batches on hand over their ${onHand} units: ${formatMoney(costCents)} each.`}
            </p>
          )}
          {rows.length > 0 ? (
            <p className="mt-2 mb-0">
              {formatMoney(total)} over {units} units: <span className="font-semibold">{formatMoney(costCents)} each</span>
            </p>
          ) : null}
          {/* When, from whom, and on which invoice and voucher each batch came (D, 10 October 2026). */}
          {bought && bought.length > 0 ? (
            <div className="mt-3 pt-2 border-t border-defaultborder">
              <p className="font-semibold mb-1">Batches bought</p>
              {bought.map((batch, index) => (
                <div key={index} className="flex justify-between gap-3 py-0.5">
                  <span className="min-w-0">
                    <span className="font-semibold">{batch.day}</span>{" "}
                    {batch.supplier ?? (batch.how === "buyback" ? "Customer buyback" : batch.how === "opening_balance" ? "Opening inventory" : batch.how === "opened_from" ? "Opened from a box" : batch.how === "gift" ? "Received free" : "")}
                    <span className="block text-[#8c9097] dark:text-white/50">
                      {[batch.purchaseNumber, batch.invoiceNo ? `Invoice ${batch.invoiceNo}` : null, batch.voucherNumber].filter(Boolean).join(" · ") || "No purchase record"}
                    </span>
                  </span>
                  <span className="text-end whitespace-nowrap">
                    {batch.units} x {formatMoney(batch.units > 0 ? Math.round(batch.costCents / batch.units) : 0)}
                    <span className="block text-[#8c9097] dark:text-white/50">
                      {formatMoney(batch.costCents)}, {batch.onHand} left
                    </span>
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** A heading between the parts of a long form. */
function FormPart({ title }: { readonly title: string }): React.ReactElement {
  return (
    <div className="col-span-12 border-b border-defaultborder pb-1 mt-2">
      <span className="text-[0.75rem] font-semibold uppercase tracking-wide text-[#8c9097] dark:text-white/50">{title}</span>
    </div>
  );
}

/**
 * What one of a product holds: so many of another product (D, 12 October 2026: "1 case = 6
 * booster boxes"). Opening it then gives those, with its cost shared over them. Left empty,
 * the product is not opened into anything but, for a box, its packs.
 */
function ContentsFields({
  id,
  itemType,
  products,
  insideId,
  insideCount,
  onInside,
  onCount,
}: {
  readonly id: string;
  readonly itemType: ItemType;
  readonly products: readonly Product[];
  readonly insideId: string;
  readonly insideCount: string;
  readonly onInside: (id: string) => void;
  readonly onCount: (count: string) => void;
}): React.ReactElement {
  const unit = typeOf(itemType).unit;
  const inside = products.find((one) => one.id === insideId);
  const packs = inside ? packsInside({ containsProductId: inside.id, containsQuantity: 1, packsPerBox: null }, products) : null;
  return (
    <>
      <Field id={`${id}-inside-count`} label={`One ${unit} holds`} className="sm:col-span-3 col-span-4">
        <input
          id={`${id}-inside-count`}
          type="text"
          inputMode="numeric"
          className="form-control"
          value={insideCount}
          placeholder="6"
          onChange={(event) => onCount(event.target.value.replace(/[^\d]/g, ""))}
        />
      </Field>
      <Field
        id={`${id}-inside`}
        label="of this product"
        className="sm:col-span-9 col-span-8"
        hint={
          inside
            ? packs && inside.itemType !== "pack"
              ? `Each ${inside.name} holds ${packs} booster packs.`
              : undefined
            : "The product inside, which must be in the catalogue already. Leave empty if it is not opened into another product."
        }
      >
        <SelectField
          id={`${id}-inside`}
          value={insideId || "none"}
          options={[
            { value: "none", label: "Nothing linked" },
            ...products.filter((one) => one.isActive && one.itemType !== "single").map((one) => ({ value: one.id, label: `${productCode(one)} ${productName(one)}` })),
          ]}
          onChange={(choice) => onInside(choice === "none" ? "" : choice)}
        />
      </Field>
    </>
  );
}

/** The catalogue as a CSV file, one product a row, named as the till names them. */
function downloadCatalogue(products: readonly Product[]): void {
  const cell = (value: string | number | null | undefined): string => {
    const text = value === null || value === undefined ? "" : String(value);
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  const money = (cents: number | null | undefined): string => (cents === null || cents === undefined ? "" : (cents / 100).toFixed(2));
  const head = ["Code", "Name on the POS", "Name", "Language", "Type", "Game", "Category", "Subcategory", "Card set", "Selling price", "Cost price", "Quantity on hand", "Tracks inventory", "Holds", "Booster packs in one", "Status", "Description"];
  const rows = products.map((product) => {
    const inside = products.find((one) => one.id === product.containsProductId);
    return [
      productCode(product),
      productName(product),
      product.name,
      product.language,
      typeOf(product.itemType).label,
      product.game,
      product.category,
      product.section,
      product.setName,
      money(product.defaultPriceCents),
      money(product.averageCostCents),
      product.onHand,
      product.tracksStock ? "Yes" : "No",
      inside && product.containsQuantity ? `${product.containsQuantity} x ${inside.name}` : "",
      packsInside({ containsProductId: product.containsProductId ?? null, containsQuantity: product.containsQuantity ?? null, packsPerBox: product.packsPerBox }, products) ?? "",
      product.isActive ? (product.notForSale ? "Not for sale" : "Active") : "Archived",
      product.description,
    ];
  });
  // A byte order mark, so Excel reads the accents and the Chinese and Japanese names.
  const text = "\uFEFF" + [head, ...rows].map((row) => row.map(cell).join(",")).join("\r\n");
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `ohc-product-catalogue-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * What a product is (D, 12 October 2026, holding a carton marked "Qty: 6 displays": "I click,
 * it tells me 6 displays, what is inside the 6 displays, the number of bundles and packs").
 * The sums come from what each product is recorded as holding, level by level. The
 * description is typed, or drafted by the reading and kept only once somebody saves it.
 */
function AboutProduct({
  product,
  products,
  canWrite,
  onClose,
  onChanged,
}: {
  readonly product: Product;
  readonly products: readonly Product[];
  /** Whether the reading is on, to draft a description. */
  readonly canWrite: boolean;
  readonly onClose: () => void;
  readonly onChanged: () => void;
}): React.ReactElement {
  const [text, setText] = useState(product.description ?? "");
  const [drafting, setDrafting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ tone: "success" | "danger" | "info"; text: string } | undefined>();

  // One line for each level: 1 case = 6 boxes; 1 box = 24 packs.
  const levels: { one: string; holds: number; of: string }[] = [];
  let at: Product | undefined = product;
  for (let depth = 0; at && depth < 8; depth++) {
    const inside: Product | undefined = products.find((one) => one.id === at?.containsProductId);
    if (inside && at.containsQuantity) {
      levels.push({ one: at.name, holds: at.containsQuantity, of: inside.name });
      at = inside;
    } else {
      if (at.packsPerBox) levels.push({ one: at.name, holds: at.packsPerBox, of: "booster packs" });
      break;
    }
  }
  const packs = packsInside({ containsProductId: product.containsProductId ?? null, containsQuantity: product.containsQuantity ?? null, packsPerBox: product.packsPerBox }, products);
  // Everything between the top and the packs, counted for one unit of this product.
  let running = 1;
  const totals = levels.map((level) => {
    running *= level.holds;
    return { count: running, of: level.of };
  });

  return (
    <Modal
      open
      title={productName(product)}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ti-btn ti-btn-light" onClick={onClose}>
            Close
          </button>
          <button
            type="button"
            className="ti-btn ti-btn-primary-full"
            disabled={busy || text.trim() === (product.description ?? "").trim()}
            onClick={() => {
              setBusy(true);
              void api
                .setProductDescription(product.id, text.trim() || null)
                .then(() => {
                  setNote({ tone: "success", text: "Description saved." });
                  onChanged();
                })
                .catch((problem: unknown) => setNote({ tone: "danger", text: problem instanceof ApiError ? problem.message : "Could not save the description" }))
                .finally(() => setBusy(false));
            }}
          >
            {busy ? <Working label="Saving" /> : "Save description"}
          </button>
        </>
      }
    >
      {note ? <Alert tone={note.tone}>{note.text}</Alert> : null}
      <div className="flex gap-3 mb-3">
        {product.photoId ? <img src={`/api/photos/${product.photoId}`} alt="" className="w-24 h-24 object-cover rounded-md border border-defaultborder shrink-0" /> : null}
        <div className="text-[0.813rem]">
          <div className="font-semibold">{typeOf(product.itemType).label}</div>
          <div className="text-[#8c9097] dark:text-white/50">
            {[productCode(product), product.game, product.setName, product.language, product.section].filter(Boolean).join(" · ")}
          </div>
          <div className="mt-1">
            {product.tracksStock ? `${product.onHand} on hand` : "Not counted"}
            {product.defaultPriceCents !== null ? ` · sells at ${formatMoney(product.defaultPriceCents)}` : ""}
          </div>
        </div>
      </div>

      <p className="form-label mb-1">What is inside</p>
      {levels.length === 0 ? (
        <p className="text-[0.813rem] text-[#8c9097] dark:text-white/50">
          Nothing is recorded as being inside it. Set what it holds, or its packs, under Edit product.
        </p>
      ) : (
        <div className="rounded-md bg-light dark:bg-bodybg px-3 py-2 text-[0.813rem] mb-3">
          {levels.map((level, index) => (
            <div key={index}>
              1 x {level.one} = <span className="font-semibold">{level.holds}</span> x {level.of}
            </div>
          ))}
          <div className="border-t border-defaultborder mt-2 pt-2 font-semibold">
            1 x {product.name} = {totals.map((total) => `${total.count} x ${total.of}`).join(" = ")}
            {packs && totals[totals.length - 1]?.of !== "booster packs" ? ` = ${packs} booster packs` : ""}
          </div>
          {product.tracksStock && product.onHand > 0 && totals.length > 0 ? (
            <div className="text-[#8c9097] dark:text-white/50 mt-1">
              The {product.onHand} on hand hold {totals.map((total) => `${total.count * product.onHand} x ${total.of}`).join(", ")}, still sealed inside.
            </div>
          ) : null}
        </div>
      )}

      <div className="flex items-center justify-between gap-2 mt-3 mb-1">
        <label htmlFor="about-description" className="form-label !mb-0">
          Description
        </label>
        {canWrite ? (
          <button
            type="button"
            className="ti-btn ti-btn-light !py-1 !px-3 !text-[0.75rem] !mb-0"
            disabled={drafting}
            onClick={() => {
              setDrafting(true);
              setNote(undefined);
              void api
                .describeProduct(product.id)
                .then((result) => {
                  setText(result.description);
                  setNote({ tone: "info", text: "Drafted by AI from the product record and its picture. Read it, change what is wrong, then save it." });
                })
                .catch((problem: unknown) => setNote({ tone: "danger", text: problem instanceof ApiError ? problem.message : "Could not write a description" }))
                .finally(() => setDrafting(false));
            }}
          >
            {drafting ? <Working label="Writing" /> : <><i className="ri-magic-line" /> Write with AI</>}
          </button>
        ) : null}
      </div>
      <textarea
        id="about-description"
        rows={6}
        className="form-control"
        value={text}
        placeholder="What it is, in a few words, for anyone who has not seen it."
        onChange={(event) => setText(event.target.value)}
      />
    </Modal>
  );
}

/** An Add product form put down half done, kept in this browser. */
interface ProductDraft {
  readonly savedAt?: string;
  readonly code: string;
  readonly name: string;
  readonly category: string;
  readonly game: string;
  readonly section: string;
  readonly held: boolean;
  readonly counted: boolean;
  readonly series: string;
  readonly language: string;
  readonly itemType: ItemType;
  readonly packs: string;
  readonly insideId: string;
  readonly insideCount: string;
  readonly price: string;
}

const PRODUCT_DRAFT = "ohc.product.draft";

function readProductDraft(): ProductDraft | undefined {
  try {
    const text = window.localStorage.getItem(PRODUCT_DRAFT);
    return text ? (JSON.parse(text) as ProductDraft) : undefined;
  } catch {
    return undefined;
  }
}

function saveProductDraft(draft: ProductDraft): void {
  try {
    window.localStorage.setItem(PRODUCT_DRAFT, JSON.stringify({ ...draft, savedAt: new Date().toISOString() }));
  } catch {
    // Nothing can be kept here, a private window say; the form closes as asked.
  }
}

function clearProductDraft(): void {
  try {
    window.localStorage.removeItem(PRODUCT_DRAFT);
  } catch {
    // Nothing was kept.
  }
}
